import {
  BufferAttribute,
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  InterleavedBufferAttribute,
  Line,
  LineSegments,
  Mesh,
  Points,
  Vector3,
} from 'three';

import { isDefined } from '../variables';

function getVector3(
  pointIndex: number,
  bufferAttribute: Float32BufferAttribute | InterleavedBufferAttribute,
) {
  return new Vector3(
    bufferAttribute.getX(pointIndex),
    bufferAttribute.getY(pointIndex),
    bufferAttribute.getZ(pointIndex),
  );
}

function compare(a?: BufferAttribute, b?: BufferAttribute): boolean {
  if (!isDefined(a) || !isDefined(b)) {
    return false;
  }

  if (a.count !== b.count) {
    return false;
  }

  for (let i = 0; i < a.array.length; i++) {
    if (a.array[i] !== b.array[i]) {
      return false;
    }
  }

  return true;
}

function subtractOffset(bufferAttribute: Float32BufferAttribute, origo: Vector3) {
  for (let i = 0; i < bufferAttribute.count; i++) {
    bufferAttribute.setXYZ(
      i,
      bufferAttribute.getX(i) - origo.x,
      bufferAttribute.getY(i) - origo.y,
      bufferAttribute.getZ(i) - origo.z,
    );
  }

  bufferAttribute.needsUpdate = true;
}

export const bufferAttributeUtils = {
  getVector3,
  compare,
  subtractOffset,
  getArray,
  getColorArray,
};

/**
 * Generic generator function that extracts attribute data from buffer attributes
 * @param object The Three.js object with geometry
 * @param attributeName The name of the attribute to extract
 * @param constructor The constructor function to create objects (Vector3, Color, etc.)
 * @returns Array of constructed objects or undefined if attribute doesn't exist
 */
function* extractAttributeData<T>(
  object:
    | Mesh<BufferGeometry, any>
    | LineSegments<BufferGeometry, any>
    | Line<BufferGeometry, any>
    | Points<BufferGeometry, any>,
  attributeName: string,
  constructor: new (x: number, y: number, z: number) => T,
): Generator<T, undefined, unknown> {
  const attribute = object.geometry.getAttribute(attributeName);
  const indexAttribute = object.geometry.getIndex();

  if (!attribute) {
    return undefined;
  }

  const isFloat32BufferAttribute =
    attribute instanceof BufferAttribute && attribute.array instanceof Float32Array;
  const isInterleavedBufferAttribute = attribute instanceof InterleavedBufferAttribute;

  if (!isFloat32BufferAttribute && !isInterleavedBufferAttribute) {
    return undefined;
  }

  if (isInterleavedBufferAttribute) {
    const stride = attribute.data.stride;
    const offset = attribute.offset;
    const array = attribute.data.array;

    if (indexAttribute) {
      // Indexed geometry - use indices to access vertices
      const indices = indexAttribute.array;
      for (const vertexIndex of indices) {
        const arrayIndex = vertexIndex * stride;
        yield new constructor(
          array[arrayIndex + offset],
          array[arrayIndex + offset + 1],
          array[arrayIndex + offset + 2],
        );
      }
    } else {
      // Non-indexed geometry - iterate through all vertices
      for (let i = 0; i < array.length; i += stride) {
        yield new constructor(array[i + offset], array[i + offset + 1], array[i + offset + 2]);
      }
    }
  }

  if (isFloat32BufferAttribute) {
    const array = attribute.array;

    if (indexAttribute) {
      // Indexed geometry - use indices to access vertices
      for (const vertexIndex of indexAttribute.array) {
        const arrayIndex = vertexIndex * 3;
        yield new constructor(array[arrayIndex], array[arrayIndex + 1], array[arrayIndex + 2]);
      }
    } else {
      // Non-indexed geometry - iterate through all vertices
      for (let i = 0; i < array.length; i += 3) {
        yield new constructor(array[i], array[i + 1], array[i + 2]);
      }
    }
  }
}

/** Gets the ordered color array from the object using the index attribute if it exists. */
function getColorArray(
  object:
    | Mesh<BufferGeometry, any>
    | LineSegments<BufferGeometry, any>
    | Line<BufferGeometry, any>
    | Points<BufferGeometry, any>,
): Color[] | undefined {
  const generator = extractAttributeData(object, 'color', Color);
  const result = Array.from(generator);
  return result.length > 0 ? result : undefined;
}

/** Gets the ordered position array from the object using the index attribute if it exists. */
function getArray(
  object:
    | Mesh<BufferGeometry, any>
    | LineSegments<BufferGeometry, any>
    | Line<BufferGeometry, any>
    | Points<BufferGeometry, any>,
  toWorld = false,
  attributeName: 'position' | 'normal' = 'position',
): Vector3[] | undefined {
  const generator = extractAttributeData(object, attributeName, Vector3);
  const positions = Array.from(generator);

  if (positions.length === 0) {
    return undefined;
  }

  if (toWorld) {
    object.updateWorldMatrix(true, false);
    positions.forEach((x) => x.applyMatrix4(object.matrixWorld));
  }

  return positions;
}

/** Gets the flat position array from the object ignoring the index attribute. */
export function getPositionArray(
  object:
    | Mesh<BufferGeometry, any>
    | LineSegments<BufferGeometry, any>
    | Line<BufferGeometry, any>
    | Points<BufferGeometry, any>,
  toWorld = false,
): Vector3[] {
  const attribute = object.geometry.getAttribute('position');
  const positions: Vector3[] = [];

  const isFloat32BufferAttribute =
    attribute instanceof BufferAttribute && attribute.array instanceof Float32Array;
  const isInterleavedBufferAttribute = attribute instanceof InterleavedBufferAttribute;

  if (!isFloat32BufferAttribute && !isInterleavedBufferAttribute) {
    throw new Error(
      'Cannot extract position array from the BufferAttribute. The BufferAttribute needs to be Float32BufferAttribute or InterleavedBufferAttribute.',
    );
  }

  if (isInterleavedBufferAttribute) {
    const stride = attribute.data.stride;
    const offset = attribute.offset;
    const array = attribute.data.array;

    for (let i = 0; i < array.length; i += stride) {
      positions.push(new Vector3(array[i + offset], array[i + offset + 1], array[i + offset + 2]));
    }
  }

  if (isFloat32BufferAttribute) {
    const array = attribute.array;

    for (let i = 0; i < array.length; i += 3) {
      positions.push(new Vector3(array[i], array[i + 1], array[i + 2]));
    }
  }

  if (toWorld) {
    // Note: localToWorld modifies the vector it's called on so no need to reassign the array.
    // object.updateMatrix();
    // object.updateMatrixWorld(true);
    object.updateWorldMatrix(true, false);
    positions.forEach((x) => object.localToWorld(x));
  }

  return positions;
}
